import moment from 'moment-timezone';
import { Dispatch } from 'redux';
import { HrpsApiClient } from '../../clients';
import {
    ExportSalesOrderDemandsInput,
    ExportSalesOrderDemandsOutput,
    GetSalesOrderDemandsOutput,
    SalesOrderDemandFilter,
    SalesOrderDemandSortDirection,
    SortOrder,
    RootState,
    GetSalesOrderDemandsInput,
    DateFilterField,
} from '../types';
import {
    SALES_ORDER_DEMAND_LIST_GET,
    SALES_ORDER_DEMAND_LIST_LOADED,
    SALES_ORDER_DEMAND_LIST_ERROR,
    SALES_ORDER_DEMAND_LIST_PAGE_CHANGE,
    SALES_ORDER_DEMAND_LIST_LOAD_MORE,
    SALES_ORDER_DEMAND_LIST_SORT_CHANGE,
    SALES_ORDER_DEMAND_LIST_FILTER_CHANGE,
    SalesOrderDemandListActions,
} from './salesOrderDemandActions';

export function salesOrderDemandListLoading(): SalesOrderDemandListActions {
    return { type: SALES_ORDER_DEMAND_LIST_GET };
}

export function salesOrderDemandListLoaded(data: GetSalesOrderDemandsOutput): SalesOrderDemandListActions {
    return { type: SALES_ORDER_DEMAND_LIST_LOADED, payload: data };
}

export function salesOrderDemandListFailed(error: any): SalesOrderDemandListActions {
    return { type: SALES_ORDER_DEMAND_LIST_ERROR, payload: { error } };
}

export function salesOrderDemandChangePage(
    currentPage: number,
    currentRecordNumber: number
): SalesOrderDemandListActions {
    return { type: SALES_ORDER_DEMAND_LIST_PAGE_CHANGE, payload: { currentPage, currentRecordNumber } };
}

export function salesOrderDemandLoadMore(
    currentPage: number,
    currentRecordNumber: number
): SalesOrderDemandListActions {
    return { type: SALES_ORDER_DEMAND_LIST_LOAD_MORE, payload: { currentPage, currentRecordNumber } };
}

export function salesOrderDemandSortChange(
    sortColumn: string,
    sortDirection: SalesOrderDemandSortDirection
): SalesOrderDemandListActions {
    return { type: SALES_ORDER_DEMAND_LIST_SORT_CHANGE, payload: { sortColumn, sortDirection } };
}

export function salesOrderDemandFilterChange(filters: {
    poIds?: string[];
    unitsStatusFilter?: string[];
    asinFilter?: string[];
    resellerSkuFilter?: string[];
    familyFilter?: string[];
    dateRange?: string[];
    shipToLocationFilter?: string[];
}): SalesOrderDemandListActions {
    return { type: SALES_ORDER_DEMAND_LIST_FILTER_CHANGE, payload: filters };
}

function buildSortConditions(
    sortColumn: string,
    sortDirection: SalesOrderDemandSortDirection,
    dateFilterField: DateFilterField
): Record<string, SortOrder> | undefined {
    const order: SortOrder = sortDirection === 'ascending' ? 'ASCENDING' : 'DESCENDING';
    switch (sortColumn) {
        case 'purchaseOrderId':
            return { PURCHASE_ORDER_ID: order };
        case 'unitsOpen':
            return { UNITS_OPEN: order };
        case 'unitsCanceled':
            return { UNITS_CANCELED: order };
        case 'unitsShipped':
            return { UNITS_SHIPPED: order };
        case 'unitsOrdered':
            return { UNITS_ORDERED: order };
        case 'unitsReleased':
            return { UNITS_RELEASED: order };
        case 'shipToLocationCode':
            return { SHIP_TO_LOCATION_CODE: order };
        case 'dateColumn':
            if (dateFilterField === 'requestedShipDate') return { REQUESTED_SHIP_DATE: order };
            if (dateFilterField === 'requestedDeliveryDate') return { REQUESTED_DELIVERY_DATE: order };
            return { CREATION_DATE: order };
        default:
            return { CREATION_DATE: order };
    }
}

export function buildFilters(
    state: RootState['salesOrderDemandReducer'],
    resellerItems: RootState['resellerReducer']['resellerItems'],
    timezone?: string
): SalesOrderDemandFilter | undefined {
    const resellerItemsData = resellerItems.value || [];
    const tz = timezone || 'UTC';
    const {
        poIds,
        unitsStatusFilter,
        asinFilter,
        resellerSkuFilter,
        familyFilter,
        shipToLocationFilter,
        dateRange,
        dateFilterField,
    } = state;

    const itemIdsFromAsin = asinFilter.length
        ? resellerItemsData.filter((ri) => asinFilter.includes(ri.asin)).map((ri) => ri.itemId)
        : undefined;

    const dateStart = dateRange[0] ? moment.tz(dateRange[0], tz).startOf('day').unix() : undefined;
    const dateEnd = dateRange[1] ? moment.tz(dateRange[1], tz).endOf('day').unix() : undefined;

    const filters: SalesOrderDemandFilter = {
        purchaseOrderIds: poIds.length ? poIds : undefined,
        unitsStatuses: unitsStatusFilter.length ? (unitsStatusFilter.map((s) => s.toUpperCase()) as any) : undefined,
        itemIds: itemIdsFromAsin,
        resellerSkus: resellerSkuFilter.length ? resellerSkuFilter : undefined,
        itemFamilies: familyFilter.length ? familyFilter : undefined,
        shipToLocationCodes: shipToLocationFilter.length ? shipToLocationFilter : undefined,
        creationDateStart: dateFilterField === 'creationDate' ? dateStart : undefined,
        creationDateEnd: dateFilterField === 'creationDate' ? dateEnd : undefined,
        requestedShipDateStart: dateFilterField === 'requestedShipDate' ? dateStart : undefined,
        requestedShipDateEnd: dateFilterField === 'requestedShipDate' ? dateEnd : undefined,
        requestedDeliveryDateStart: dateFilterField === 'requestedDeliveryDate' ? dateStart : undefined,
        requestedDeliveryDateEnd: dateFilterField === 'requestedDeliveryDate' ? dateEnd : undefined,
    };

    return Object.values(filters).some((v) => v !== undefined) ? filters : undefined;
}

export function getSalesOrderDemandList(resellerId: string, startAt: number, maxResults: number) {
    return (dispatch: Dispatch, getState: () => RootState) => {
        const state = getState();
        const { sortColumn, sortDirection, dateFilterField } = state.salesOrderDemandReducer;
        const timezone = state.resellerReducer.resellerDetails.value?.resellerIanaTimezone || undefined;

        dispatch(salesOrderDemandListLoading());

        return new HrpsApiClient()
            .callHrps('getSalesOrderDemands', {
                resellerId,
                maxResults,
                startAtResult: startAt,
                sortConditions: buildSortConditions(sortColumn, sortDirection, dateFilterField),
                filters: buildFilters(state.salesOrderDemandReducer, state.resellerReducer.resellerItems, timezone),
            } as GetSalesOrderDemandsInput)
            .then((response) => {
                dispatch(salesOrderDemandListLoaded(response.data as GetSalesOrderDemandsOutput));
            })
            .catch((error) => {
                dispatch(salesOrderDemandListFailed(error));
            });
    };
}

export function getSalesOrderDemandById(resellerId: string, purchaseOrderId: string) {
    return new HrpsApiClient()
        .callHrps('getSalesOrderDemands', {
            resellerId,
            maxResults: 1,
            startAtResult: 0,
            filters: { purchaseOrderIds: [purchaseOrderId] },
        })
        .then((response) => {
            const data = response.data as GetSalesOrderDemandsOutput;
            return data.salesOrderDemands?.[0] || null;
        });
}

export async function exportSalesOrderDemands(
    resellerId: string,
    filters?: SalesOrderDemandFilter,
    locale?: string,
    resellerIanaTimezone?: string
): Promise<ExportSalesOrderDemandsOutput> {
    const response = await new HrpsApiClient().callHrps('exportSalesOrderDemands', {
        resellerId,
        filters,
        locale,
        resellerIanaTimezone,
    } as ExportSalesOrderDemandsInput);
    return response.data as ExportSalesOrderDemandsOutput;
}
